import { useEffect, useRef, useState } from "react";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "@/Utils/firebase";
import {
  redeemTicket,
  renewTicket,
  RedeemErrorCode,
} from "@/Utils/redeemClient";
import { logInfo, logError } from "@/Utils/logger";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { Spinner } from "@/Components/Spinner";

import "./redeemLink.scss";

type Phase =
  | { kind: "working" }
  | { kind: "error"; code: RedeemErrorCode }
  | { kind: "renew-sent" }
  | { kind: "renew-failed" };

const RedeemLink = () => {
  const { navigateTo } = useCustomNavigation();
  const [phase, setPhase] = useState<Phase>({ kind: "working" });
  // Ticket survives ONLY in memory after we strip it from the URL/history.
  const ticketRef = useRef<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // StrictMode double-invoke guard
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const ticket = params.get("ticket");
    // Strip the ticket from URL + history BEFORE any network call.
    window.history.replaceState({}, document.title, "/redeem");

    if (!ticket) {
      setPhase({ kind: "error", code: "TICKET_INVALID" });
      return;
    }
    ticketRef.current = ticket;

    (async () => {
      logInfo("[magic-link] redeem start");
      const result = await redeemTicket(ticket);
      if (!result.ok) {
        logInfo("[magic-link] redeem rejected", { code: result.code });
        setPhase({ kind: "error", code: result.code });
        return;
      }
      try {
        const cred = await signInWithCustomToken(auth, result.data.customToken);
        // iOS / WKWebView can resolve WITHOUT setting auth.currentUser — always
        // use the RETURNED cred.user (HERE_BE_DRAGONS §Auth flow). uid is used
        // for logging only; Firestore reads pick up the signed-in user.
        logInfo("[magic-link] signed in as scan owner", {
          uidPrefix: cred.user.uid.slice(0, 8),
          pipelineStatus: result.data.pipelineStatus ?? "unknown",
        });
        if (result.data.pipelineStatus === "APPROVED") {
          const ts = result.data.timestamp;
          const date = ts ? new Date(ts * 1000).toLocaleDateString() : "";
          // Mirror the state shape AvatarScreen passes to /avatar-preview
          // (src/Pages/AvatarScreen/index.tsx:319) — { id, name, date }, which
          // AvatarPreview reads at src/Pages/AvatarPreview/index.jsx:41.
          navigateTo("/avatar-preview", {
            state: {
              id: result.data.scanId,
              name: result.data.name || "",
              date,
            },
          });
        } else {
          // Processing / failed / unknown: land on home. Its list is keyed by
          // uid (useScansList), so it loads the owner's scans, renders
          // per-status cards, and watches a scan still being processed until
          // it is approved or rejected. Signing in as the owner makes it
          // "just work".
          navigateTo("/");
        }
      } catch (e) {
        logError("[magic-link] signInWithCustomToken threw", {
          name: (e as Error)?.name,
          message: (e as Error)?.message,
        });
        // NO anon fallback here — by design (a rescue-anon uid ≠ scan owner).
        setPhase({ kind: "error", code: "NETWORK" });
      }
    })();
  }, [navigateTo]);

  const requestNewLink = async () => {
    const ticket = ticketRef.current;
    if (!ticket) return;
    setPhase({ kind: "working" });
    const r = await renewTicket(ticket);
    if (r.ok) setPhase({ kind: "renew-sent" });
    else setPhase({ kind: "renew-failed" });
  };

  if (phase.kind === "working") {
    return (
      <div className="redeem-container">
        <Spinner />
        <p>Opening your scan…</p>
      </div>
    );
  }
  if (phase.kind === "renew-sent") {
    return (
      <div className="redeem-container">
        <h2>Check your email</h2>
        <p>
          We sent a fresh link to the email on this scan. You can close this
          tab.
        </p>
      </div>
    );
  }
  if (phase.kind === "renew-failed") {
    return (
      <div className="redeem-container">
        <h2>Could not send a new link</h2>
        <p>Please try again later or contact support.</p>
      </div>
    );
  }
  // phase.kind === "error"
  const renewable = phase.code === "TICKET_EXPIRED";
  return (
    <div className="redeem-container">
      <h2>{renewable ? "This link has expired" : "This link isn't valid"}</h2>
      {renewable ? (
        <button type="button" onClick={requestNewLink}>
          Email me a new link
        </button>
      ) : (
        <p>Please use the most recent link from your email.</p>
      )}
    </div>
  );
};

export default RedeemLink;
